import React, { useEffect } from 'react';
import { getTeacherDetails } from '../../../redux/teacherRelated/teacherHandle';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
    Button, Container, Typography, Box, Paper, Avatar, 
    Divider, Grid, Chip, IconButton, CircularProgress 
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import styled, { keyframes } from 'styled-components';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SchoolIcon from '@mui/icons-material/School';
import MenuBookIcon from '@mui/icons-material/MenuBook';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const TeacherDetails = () => {
    const navigate = useNavigate();
    const params = useParams();
    const dispatch = useDispatch();
    const { loading, teacherDetails } = useSelector((state) => state.teacher);

    const teacherID = params.id;

    useEffect(() => {
        dispatch(getTeacherDetails(teacherID));
    }, [dispatch, teacherID]);

    

    const isSubjectNamePresent = teacherDetails?.teachSubject?.subName;

    const handleAddSubject = () => {
        navigate(`/Admin/teachers/choosesubject/${teacherDetails?.teachSclass?._id}/${teacherDetails?._id}`);
    };

    return (
        <PageContainer>
            <StickyHeader>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => navigate(-1)} sx={{ color: 'white' }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        Teacher Profile
                    </Typography>
                </Box>
            </StickyHeader>

            {loading ? (
                <LoadingBox>
                    <CircularProgress size={60} />
                    <Typography sx={{ mt: 2 }}>Loading profile...</Typography>
                </LoadingBox>
            ) : (
                <MainContent>
                    <Grid container spacing={4}>
                        {/* Profile Info Card */}
                        <Grid item xs={12} md={4}>
                            <InfoCard elevation={3}>
                                <ProfileHeader>
                                    <Avatar 
                                        src={teacherDetails?.photo ? `${process.env.REACT_APP_BASE_URL}/${teacherDetails.photo}` : ""}
                                        sx={{ width: 120, height: 120, mb: 2, border: '4px solid white', boxShadow: 2 }}
                                    >
                                        {teacherDetails?.name?.charAt(0)}
                                    </Avatar>
                                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                                        {teacherDetails?.name}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                        Registered Teacher
                                    </Typography>
                                </ProfileHeader>
                                <Divider sx={{ my: 2 }} />
                                <ContactInfo>
                                    <InfoItem>
                                        <EmailIcon color="primary" fontSize="small" />
                                        <Typography variant="body2">{teacherDetails?.email}</Typography>
                                    </InfoItem>
                                    <InfoItem>
                                        <PhoneIcon color="primary" fontSize="small" />
                                        <Typography variant="body2">{teacherDetails?.phone || 'No phone provided'}</Typography>
                                    </InfoItem>
                                    <InfoItem>
                                        <LocationOnIcon color="primary" fontSize="small" />
                                        <Typography variant="body2">{teacherDetails?.address || 'No address provided'}</Typography>
                                    </InfoItem>
                                </ContactInfo>
                            </InfoCard>
                        </Grid>

                        {/* Professional Details Card */}
                        <Grid item xs={12} md={8}>
                            <DetailsPaper elevation={3}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <SchoolIcon color="primary" /> Professional Assignments
                                    </Typography>
                                    
                                </Box>
                                
                                {teacherDetails?.assignments && teacherDetails.assignments.length > 0 ? (
                                    <Grid container spacing={2}>
                                        {teacherDetails.assignments.map((assignment, index) => (
                                            <Grid item xs={12} key={index}>
                                                <AssignmentCard>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <Avatar sx={{ bgcolor: '#e8eaf6', color: '#3f51b5' }}>
                                                            <SchoolIcon fontSize="small" />
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                                Class: {assignment.sclass?.sclassName || 'N/A'}
                                                            </Typography>
                                                            <Typography variant="body2" color="textSecondary">
                                                                Subject: {assignment.subject?.subName || 'N/A'} ({assignment.subject?.subCode || 'N/A'})
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    <Box sx={{ textAlign: 'right' }}>
                                                        <Chip 
                                                            size="small" 
                                                            label={`${assignment.subject?.sessions || 0} Sessions`} 
                                                            sx={{ bgcolor: '#f0f2f5', fontWeight: 600 }}
                                                        />
                                                    </Box>
                                                </AssignmentCard>
                                            </Grid>
                                        ))}
                                    </Grid>
                                ) : (
                                    <>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} sm={6}>
                                                <StatBox>
                                                    <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', fontWeight: 700 }}>
                                                        Assigned Class
                                                    </Typography>
                                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                        {teacherDetails?.teachSclass?.sclassName || 'Not Assigned'}
                                                    </Typography>
                                                </StatBox>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <StatBox>
                                                    <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', fontWeight: 700 }}>
                                                        Assigned Subject
                                                    </Typography>
                                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                        {teacherDetails?.teachSubject?.subName || 'Not Assigned'}
                                                    </Typography>
                                                </StatBox>
                                            </Grid>
                                        </Grid>

                                        <Divider sx={{ my: 4 }} />

                                        <Box sx={{ p: 3, bgcolor: '#f8f9fa', borderRadius: '16px' }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <MenuBookIcon color="primary" /> Subject Details
                                            </Typography>
                                            {isSubjectNamePresent ? (
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={6}>
                                                        <Typography variant="body2" color="textSecondary">Subject Code</Typography>
                                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{teacherDetails?.teachSubject?.subCode || 'N/A'}</Typography>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <Typography variant="body2" color="textSecondary">Sessions</Typography>
                                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{teacherDetails?.teachSubject?.sessions || '0'}</Typography>
                                                    </Grid>
                                                </Grid>
                                            ) : (
                                                <Box sx={{ textAlign: 'center', py: 2 }}>
                                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                                        No subject has been assigned to this teacher yet.
                                                    </Typography>
                                                    <Button 
                                                        variant="contained" 
                                                        onClick={handleAddSubject}
                                                        sx={{ 
                                                            borderRadius: '10px',
                                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                            textTransform: 'none'
                                                        }}
                                                    >
                                                        Assign Subject Now
                                                    </Button>
                                                </Box>
                                            )}
                                        </Box>
                                    </>
                                )}
                            </DetailsPaper>
                        </Grid>
                    </Grid>
                </MainContent>
            )}
        </PageContainer>
    );
};

export default TeacherDetails;

const PageContainer = styled.div`
    background-color: #f0f2f5;
    min-height: 100vh;
`;

const StickyHeader = styled(Box)`
    position: sticky;
    top: 0;
    z-index: 1000;
    background: linear-gradient(to right, #1a1a2e, #16213e);
    color: white;
    padding: 16px 40px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
`;

const MainContent = styled(Container)`
    padding-top: 40px;
    padding-bottom: 40px;
    animation: ${fadeIn} 0.6s ease-out;
`;

const InfoCard = styled(Paper)`
    padding: 32px;
    border-radius: 24px !important;
    text-align: center;
`;

const ProfileHeader = styled(Box)`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const ContactInfo = styled(Box)`
    display: flex;
    flex-direction: column;
    gap: 16px;
    text-align: left;
    margin-top: 20px;
`;

const InfoItem = styled(Box)`
    display: flex;
    align-items: center;
    gap: 12px;
    color: #444;
`;

const DetailsPaper = styled(Paper)`
    padding: 40px;
    border-radius: 24px !important;
    height: 100%;
`;

const StatBox = styled(Box)`
    background-color: #f8f9fa;
    padding: 24px;
    border-radius: 20px;
    border: 1px solid #eee;
    transition: all 0.3s ease;
    
    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 24px rgba(0,0,0,0.05);
        border-color: #667eea;
    }
`;

const AssignmentCard = styled(Box)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    background-color: #f8f9fa;
    border-radius: 16px;
    border: 1px solid #eee;
    transition: all 0.2s ease;
    
    &:hover {
        background-color: #fff;
        border-color: #667eea;
        box-shadow: 0 4px 12px rgba(0,0,0,0.05);
    }
`;

const LoadingBox = styled(Box)`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 80vh;
    color: #667eea;
`;